// KAfrequency PWA service-worker registration wrapper.
//
// Refuses to register in dev, Lovable preview/iframe, or when the URL carries
// ?sw=off. In any refused context it also unregisters any stale /sw.js so the
// old cached shell can't strand a user. The messaging worker (/sw-push.js) is
// managed separately by src/lib/push.ts and left untouched here.

const APP_SW_URL = "/sw.js";

function isPreviewOrDevHost(): boolean {
  if (typeof window === "undefined") return true;
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  if (hostname.startsWith("id-preview--") || hostname.startsWith("preview--")) return true;
  if (hostname === "lovableproject.com" || hostname.endsWith(".lovableproject.com")) return true;
  if (hostname === "lovableproject-dev.com" || hostname.endsWith(".lovableproject-dev.com")) return true;
  if (hostname === "beta.lovable.dev" || hostname.endsWith(".beta.lovable.dev")) return true;
  return false;
}

function inIframe(): boolean {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch {
    return true;
  }
}

function killSwitchOn(): boolean {
  if (typeof window === "undefined") return false;
  return new URL(window.location.href).searchParams.get("sw") === "off";
}

async function unregisterAppSw(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(
      regs
        .filter((r) => {
          const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || "";
          return url.endsWith(APP_SW_URL);
        })
        .map((r) => r.unregister()),
    );
  } catch {
    /* ignore */
  }
}

export async function registerAppServiceWorker(): Promise<void> {
  if (typeof window === "undefined" || typeof navigator === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Only in real production builds.
  if (!import.meta.env.PROD) return;
  if (inIframe() || isPreviewOrDevHost() || killSwitchOn()) {
    await unregisterAppSw();
    return;
  }

  try {
    const reg = await navigator.serviceWorker.register(APP_SW_URL, { scope: "/" });

    // Update-available detection. When a new SW enters `waiting`, dispatch a
    // window event so a UI banner can offer to reload. The banner lives in
    // <ServiceWorkerUpdatePrompt> mounted at the root.
    const notify = () => window.dispatchEvent(new CustomEvent("kaf:sw-update-ready"));
    if (reg.waiting) notify();
    reg.addEventListener("updatefound", () => {
      const nw = reg.installing;
      if (!nw) return;
      nw.addEventListener("statechange", () => {
        if (nw.state === "installed" && navigator.serviceWorker.controller) notify();
      });
    });

    // When the new SW takes control, reload once so the user sees the fresh shell.
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });

    // Periodic update check while the tab stays open.
    setInterval(() => { reg.update().catch(() => {}); }, 60 * 60 * 1000);
  } catch (err) {
    // Non-fatal — the app still works without offline support.
    console.warn("[pwa] service worker registration failed", err);
  }
}

/** Ask the waiting worker to activate; controllerchange listener reloads. */
export async function activatePendingUpdate(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration("/");
  reg?.waiting?.postMessage({ type: "SKIP_WAITING" });
}
